import { Input as AntInput, InputProps as AntInputProps, type InputRef, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { forwardRef } from 'react';
import {
  ComponentSize,
  getControlHeight,
  getBorderRadius,
} from '../../utils/tokenHelpers';
import { type InputStatus, getInputStatusStyles, getHelpTextColor } from '../../utils/statusStyles';

export interface InputProps extends Omit<AntInputProps, 'status' | 'size'> {
  /** Input status for validation feedback */
  status?: InputStatus;
  /** Label text */
  label?: string;
  /** Help text below the input */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Input size following Figma design system */
  size?: ComponentSize;
}

interface StyledInputProps {
  $token: GlobalToken;
  $status: InputStatus;
  $height: number;
  $borderRadius: number;
}

const Wrapper = styled.div<{ $token: GlobalToken }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $token }) => $token.marginXXS}px;
  width: 100%;
`;

const LabelWrapper = styled.label<{ $token: GlobalToken }>`
  display: flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS}px;
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 400;
  color: ${({ $token }) => $token.colorText};
`;

const RequiredMark = styled.span<{ $token: GlobalToken }>`
  color: ${({ $token }) => $token.colorError};
`;

const HelpText = styled.span<{ $token: GlobalToken; $status: InputStatus }>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token, $status }) => getHelpTextColor($token, $status)};
`;

const StyledInput = styled(AntInput)<StyledInputProps>`
  font-family: ${({ $token }) => $token.fontFamily};
  font-size: ${({ $token }) => $token.fontSize}px;
  height: ${({ $height }) => $height}px;
  padding: ${({ $token }) => `${$token.paddingXS}px ${$token.paddingSM}px`};
  border-radius: ${({ $borderRadius }) => $borderRadius}px;
  border: ${({ $token }) => $token.lineWidth}px solid;
  transition: all ${({ $token }) => $token.motionDurationMid} ease;

  ${({ $token, $status }) => getInputStatusStyles($token, $status)}

  &:disabled {
    background: ${({ $token }) => $token.colorBgLayout};
    border-color: ${({ $token }) => $token.colorBorderSecondary};
    color: ${({ $token }) => $token.colorTextDisabled};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ $token }) => $token.colorTextTertiary};
  }

  /* Affix icons */
  .ant-input-prefix,
  .ant-input-suffix {
    color: ${({ $token }) => $token.colorTextSecondary};
  }
`;

/**
 * Input component based on Figma Design System
 *
 * Now uses Ant Design theme tokens for consistent styling and size support.
 *
 * @example
 * ```tsx
 * import { Input } from '@libertify/ui';
 *
 * <Input label="Email" placeholder="Enter your email" />
 * <Input label="Password" required status="error" helpText="Password is required" />
 * <Input status="success" helpText="Username is available" />
 * <Input size="s" placeholder="Small input" />
 * <Input size="l" placeholder="Large input" />
 * ```
 */
export const Input = forwardRef<InputRef, InputProps>(({
  status = 'default',
  label,
  helpText,
  required,
  size = 'm',
  ...props
}, ref) => {
  const { token } = theme.useToken();
  const height = getControlHeight(token, size);
  const borderRadius = getBorderRadius(token, size);

  return (
    <Wrapper $token={token}>
      {label && (
        <LabelWrapper $token={token}>
          {label}
          {required && <RequiredMark $token={token}>*</RequiredMark>}
        </LabelWrapper>
      )}
      <StyledInput
        ref={ref}
        $token={token}
        $status={status}
        $height={height}
        $borderRadius={borderRadius}
        {...props}
      />
      {helpText && (
        <HelpText $token={token} $status={status}>
          {helpText}
        </HelpText>
      )}
    </Wrapper>
  );
});

export default Input;
